import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  createOrder,
  deleteOrder,
  emailOrderStatus,
  getAllBooks,
  getAllClients,
  getAllOrders,
  getOrderById,
  getPendingOrderSummary,
  updateOrderItems,
  updateOrderStatus,
} from '../utils/api';
import Header from '../components/Header';

interface Book {
  _id: string;
  title: string;
  cost: number;
  quantityInStock: number;
  sku?: string;
}

interface Person {
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface OrderItem {
  bookId: string | Book;
  quantity: number;
  discountedPrice: number;
}

interface StatusHistory {
  status: string;
  note?: string;
  description: string;
  changedAt: string;
  changedBy?: string;
}

interface Order {
  _id: string;
  orderNumber?: string;
  clientId?: string | Person;
  userId?: string | Person;
  customerSnapshot?: Person;
  items: OrderItem[];
  totalAmount: number;
  status: string;
  statusHistory?: StatusHistory[];
  billingAddress?: Address;
  shippingAddress?: Address;
  createdAt: string;
  updatedAt: string;
  stockDeductedAt?: string;
  stockRestoredAt?: string;
}

interface Address {
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

interface Client extends Person {
  _id: string;
  clientCode: string;
  name: string;
}

interface PendingSummary {
  bookId: string;
  title: string;
  sku?: string;
  totalQuantity: number;
  orderCount: number;
  quantityInStock: number;
}

const blankItem = (): OrderItem => ({ bookId: '', quantity: 1, discountedPrice: 0 });
const bookIdOf = (item: OrderItem) => typeof item.bookId === 'string' ? item.bookId : item.bookId?._id;
const bookOf = (item: OrderItem, books: Book[]) => {
  const fullBook = books.find((book) => book._id === bookIdOf(item));
  return fullBook || (typeof item.bookId === 'object' ? item.bookId : undefined);
};

const ORDER_STATUSES = ['pending', 'confirmed', 'payment_received', 'shipped', 'cancelled', 'completed'];
const STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ['confirmed', 'cancelled'],
  processing: ['payment_received', 'shipped', 'cancelled'],
  confirmed: ['payment_received', 'shipped', 'cancelled'],
  payment_received: ['shipped', 'completed', 'cancelled'],
  shipped: ['payment_received', 'completed', 'cancelled'],
  cancelled: [],
  completed: [],
};
const statusLabel = (status: string) => status.split('_').map((part) => part[0].toUpperCase() + part.slice(1)).join(' ');

const customerFor = (order: Order): Person => {
  if (order.clientId && typeof order.clientId === 'object') return order.clientId;
  if (order.userId && typeof order.userId === 'object') return order.userId;
  return order.customerSnapshot || {};
};

const formatAddress = (address?: Address) =>
  address ? [address.address, address.city, address.state, address.pincode].filter(Boolean).join(', ') : 'Not recorded';

const buildPendingSummary = (orders: Order[], books: Book[]): PendingSummary[] => {
  const summary = new Map<string, PendingSummary>();

  orders
    .filter((order) => ['pending', 'confirmed', 'payment_received', 'processing'].includes(order.status))
    .forEach((order) => {
      order.items.forEach((item) => {
        const bookId = bookIdOf(item);
        if (!bookId) return;
        const book = bookOf(item, books);
        const current = summary.get(bookId);
        summary.set(bookId, {
          bookId,
          title: book?.title || current?.title || 'Unknown book',
          sku: book?.sku || current?.sku,
          totalQuantity: (current?.totalQuantity || 0) + item.quantity,
          orderCount: (current?.orderCount || 0) + 1,
          quantityInStock: book?.quantityInStock ?? current?.quantityInStock ?? 0,
        });
      });
    });

  return [...summary.values()].sort((left, right) => right.totalQuantity - left.totalQuantity);
};

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [pendingSummary, setPendingSummary] = useState<PendingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([blankItem()]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [editItems, setEditItems] = useState<OrderItem[]>([]);
  const [statusChange, setStatusChange] = useState<{ order: Order; status: string } | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [statusDescription, setStatusDescription] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setError('');
    try {
      const [ordersData, clientsData, booksData, summaryData] = await Promise.all([
        getAllOrders(),
        getAllClients(),
        getAllBooks(),
        getPendingOrderSummary().catch((summaryError: any) => {
          if (summaryError.response?.status === 404) return null;
          throw summaryError;
        }),
      ]);
      setOrders(ordersData || []);
      setClients(clientsData || []);
      setBooks(booksData || []);
      setPendingSummary(summaryData || buildPendingSummary(ordersData || [], booksData || []));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const visibleOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = orders.filter((order) => {
      const customer = customerFor(order);
      const matchesQuery = !query || [
        order.orderNumber,
        order._id,
        customer.name,
        customer.email,
        customer.phone,
      ].some((value) => String(value || '').toLowerCase().includes(query));
      return matchesQuery && (statusFilter === 'all' || order.status === statusFilter);
    });

    return [...filtered].sort((left, right) => {
      if (sortBy === 'oldest') return +new Date(left.createdAt) - +new Date(right.createdAt);
      if (sortBy === 'client') return String(customerFor(left).name || '').localeCompare(String(customerFor(right).name || ''));
      if (sortBy === 'order') return String(left.orderNumber || left._id).localeCompare(String(right.orderNumber || right._id));
      return +new Date(right.createdAt) - +new Date(left.createdAt);
    });
  }, [orders, search, sortBy, statusFilter]);

  const lineTotal = (item: OrderItem) => {
    const book = bookOf(item, books);
    const unitPrice = item.discountedPrice > 0 ? item.discountedPrice : Number(book?.cost || 0);
    return unitPrice * item.quantity;
  };

  const updateItem = (
    items: OrderItem[],
    setItems: React.Dispatch<React.SetStateAction<OrderItem[]>>,
    index: number,
    field: 'bookId' | 'quantity' | 'discountedPrice',
    value: string
  ) => {
    const next = items.map((item) => ({ ...item }));
    if (field === 'bookId') next[index].bookId = value;
    if (field === 'quantity') next[index].quantity = Math.max(1, Number.parseInt(value, 10) || 1);
    if (field === 'discountedPrice') next[index].discountedPrice = Math.max(0, Number.parseFloat(value) || 0);
    setItems(next);
  };

  const validateItems = (items: OrderItem[]) => {
    if (!items.length || items.some((item) => !bookIdOf(item) || item.quantity < 1)) {
      setError('Add at least one valid item to the order.');
      return false;
    }
    const ids = items.map(bookIdOf);
    if (new Set(ids).size !== ids.length) {
      setError('The same book cannot appear twice. Increase its quantity instead.');
      return false;
    }
    return true;
  };

  const handleCreateOrder = async () => {
    setError('');
    if (!selectedClient) return setError('Please select a client.');
    if (!validateItems(orderItems)) return;

    setSaving(true);
    try {
      await createOrder({
        clientId: selectedClient,
        items: orderItems.map((item) => ({
          bookId: bookIdOf(item),
          quantity: item.quantity,
          discountedPrice: item.discountedPrice,
        })),
      });
      setSuccess('Order created successfully.');
      setSelectedClient('');
      setOrderItems([blankItem()]);
      setShowForm(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  const openDetails = async (orderId: string) => {
    setError('');
    try {
      setSelectedOrder(await getOrderById(orderId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load order details');
    }
  };

  const openEditor = async (order: Order) => {
    setError('');
    try {
      const detailed = await getOrderById(order._id);
      setEditingOrder(detailed);
      setEditItems(detailed.items.map((item: OrderItem) => ({
        bookId: bookIdOf(item),
        quantity: item.quantity,
        discountedPrice: item.discountedPrice,
      })));
      setSelectedOrder(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to open order editor');
    }
  };

  const saveEditedItems = async () => {
    if (!editingOrder || !validateItems(editItems)) return;
    setSaving(true);
    setError('');
    try {
      await updateOrderItems(editingOrder._id, editItems.map((item) => ({
        bookId: bookIdOf(item),
        quantity: item.quantity,
        discountedPrice: item.discountedPrice,
      })));
      setSuccess('Order items updated. Stock will be deducted when the order is shipped.');
      setEditingOrder(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const submitStatusChange = async () => {
    if (!statusChange) return;
    if (!statusNote.trim()) {
      setError('Please enter a short status note.');
      return;
    }
    if (!statusDescription.trim()) {
      setError('Please enter a description for the status update.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateOrderStatus(statusChange.order._id, statusChange.status, statusNote.trim(), statusDescription.trim());
      setSuccess('Order status updated and recorded in its history.');
      setStatusChange(null);
      setStatusNote('');
      setStatusDescription('');
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update order status');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailStatus = async (order: Order) => {
    setError('');
    try {
      const result = await emailOrderStatus(order._id);
      setSuccess(result.message || 'Order status email sent.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send status email');
    }
  };

  const handleDeleteOrder = async (order: Order) => {
    if (!window.confirm(`Delete order ${order.orderNumber || order._id}?`)) return;
    try {
      await deleteOrder(order._id);
      setSuccess('Order deleted successfully.');
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete order');
    }
  };

  const generateOrderPdf = (order: Order) => {
    const customer = customerFor(order);
    const doc = new jsPDF();
    let y = 18;
    const write = (text: string, size = 11, weight: 'normal' | 'bold' = 'normal') => {
      doc.setFontSize(size);
      doc.setFont('helvetica', weight);
      const lines = doc.splitTextToSize(text, 180);
      if (y + lines.length * 6 > 280) { doc.addPage(); y = 18; }
      doc.text(lines, 15, y);
      y += lines.length * 6;
    };

    write('DSS ORDER', 20, 'bold');
    write(`Order: ${order.orderNumber || order._id}`, 13, 'bold');
    write(`Created: ${new Date(order.createdAt).toLocaleString()}`);
    write(`Status: ${order.status.toUpperCase()}`);
    write(`Customer: ${customer.name || 'Unknown'} | ${customer.email || ''} | ${customer.phone || ''}`);
    write(`Shipping: ${formatAddress(order.shippingAddress)}`);
    y += 4;
    write('Items', 14, 'bold');
    order.items.forEach((item, index) => {
      const book = bookOf(item, books);
      write(`${index + 1}. ${book?.title || 'Book'} — ${item.quantity} x Rs. ${item.discountedPrice.toFixed(2)} = Rs. ${(item.quantity * item.discountedPrice).toFixed(2)}`);
    });
    write(`Total: Rs. ${order.totalAmount.toFixed(2)}`, 14, 'bold');
    y += 4;
    write('Status history', 14, 'bold');
    (order.statusHistory || []).forEach((entry) => {
      write(`${new Date(entry.changedAt).toLocaleString()} — ${statusLabel(entry.status)}: ${entry.note ? `${entry.note} — ` : ''}${entry.description}${entry.changedBy ? ` (${entry.changedBy})` : ''}`);
    });
    doc.save(`${order.orderNumber || order._id}.pdf`);
  };

  const renderItemsEditor = (items: OrderItem[], setItems: React.Dispatch<React.SetStateAction<OrderItem[]>>) => (
    <>
      <div className="invoice-table-container">
        <table className="invoice-table">
          <thead><tr><th>Book</th><th>Stock</th><th>Quantity</th><th>Unit price</th><th>Total</th><th /></tr></thead>
          <tbody>
            {items.map((item, index) => {
              const book = bookOf(item, books);
              return (
                <tr key={`${bookIdOf(item)}-${index}`}>
                  <td>
                    <select className="item-select" value={bookIdOf(item)} onChange={(event) => updateItem(items, setItems, index, 'bookId', event.target.value)}>
                      <option value="">Select book</option>
                      {books.map((option) => <option key={option._id} value={option._id}>{option.title}</option>)}
                    </select>
                  </td>
                  <td>{book?.quantityInStock ?? '-'}</td>
                  <td><input className="qty-input" type="number" min="1" value={item.quantity} onChange={(event) => updateItem(items, setItems, index, 'quantity', event.target.value)} /></td>
                  <td><input className="price-input" type="number" min="0" step="0.01" value={item.discountedPrice} placeholder={String(book?.cost || 0)} onChange={(event) => updateItem(items, setItems, index, 'discountedPrice', event.target.value)} /></td>
                  <td>₹{lineTotal(item).toFixed(2)}</td>
                  <td><button className="btn-remove" type="button" onClick={() => setItems(items.filter((_, itemIndex) => itemIndex !== index))}>Remove</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="editor-footer">
        <button className="btn-add-item" type="button" onClick={() => setItems([...items, blankItem()])}>+ Add item</button>
        <strong>Total: ₹{items.reduce((sum, item) => sum + lineTotal(item), 0).toFixed(2)}</strong>
      </div>
    </>
  );

  if (loading) return <><Header title="Order Management" /><div className="page-container"><p>Loading orders...</p></div></>;

  return (
    <>
      <Header title="Order Management" />
      <div className="page-container">
        <div className="page-header">
          <div><h1>Orders</h1><p className="page-subtitle">Manage fulfillment, inventory, history, and invoices.</p></div>
          <button className="btn-primary" onClick={() => setShowForm((current) => !current)}>{showForm ? 'Cancel' : '+ Create New Order'}</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <section className="status-sequence card card-outline card-primary">
          <div className="card-body">
            <strong>Order status sequence</strong>
            <div className="sequence-flow"><span>Pending</span><i className="bi bi-arrow-right" /><span>Confirmed</span><i className="bi bi-arrow-right" /><span>Payment Received or Shipped</span><i className="bi bi-arrow-right" /><span>Completed</span></div>
            <small>Payment may be recorded before or after shipping. Cancellation is available before completion. Stock is deducted when Shipped is recorded.</small>
          </div>
        </section>

        <section className="order-summary-card">
          <div className="section-heading"><div><h2>Pending fulfillment summary</h2><p>Required quantities before packing: pending, confirmed, and payment-received orders.</p></div><span>{pendingSummary.reduce((sum, item) => sum + item.totalQuantity, 0)} units</span></div>
          <div className="summary-table-wrap">
            <table className="table compact-table">
              <thead><tr><th>Book</th><th>SKU</th><th>Orders</th><th>Required</th><th>In stock</th><th>Shortfall</th></tr></thead>
              <tbody>{pendingSummary.map((item) => <tr key={item.bookId}><td>{item.title}</td><td>{item.sku || '—'}</td><td>{item.orderCount}</td><td><strong>{item.totalQuantity}</strong></td><td>{item.quantityInStock}</td><td className={item.totalQuantity > item.quantityInStock ? 'shortfall' : ''}>{Math.max(0, item.totalQuantity - item.quantityInStock)}</td></tr>)}</tbody>
            </table>
            {!pendingSummary.length && <p className="no-data">No pending items.</p>}
          </div>
        </section>

        {showForm && <section className="form-container invoice-form">
          <h2>Create new order</h2>
          <div className="form-group"><label>Client</label><select value={selectedClient} onChange={(event) => setSelectedClient(event.target.value)}><option value="">Select client</option>{clients.map((client) => <option key={client._id} value={client._id}>[{client.clientCode}] {client.name}</option>)}</select></div>
          {renderItemsEditor(orderItems, setOrderItems)}
          <div className="form-actions"><button disabled={saving} className="btn-primary" onClick={handleCreateOrder}>{saving ? 'Creating...' : 'Create order'}</button><button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button></div>
        </section>}

        <section className="orders-section">
          <div className="section-heading"><div><h2>All orders</h2><p>{visibleOrders.length} of {orders.length} orders</p></div></div>
          <div className="order-toolbar">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search client, email, phone, order ID..." />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All statuses</option>{ORDER_STATUSES.map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}</select>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="client">Client A–Z</option><option value="order">Order ID</option></select>
          </div>
          <div className="table-container order-table-scroll">
            <table className="table">
              <thead><tr><th>Order ID</th><th>Client</th><th>Items</th><th>Total</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>{visibleOrders.map((order) => {
                const customer = customerFor(order);
                return <tr key={order._id}>
                  <td className="order-id">{order.orderNumber || order._id.slice(0, 8)}</td>
                  <td><strong>{customer.name || 'Unknown customer'}</strong><small>{customer.email || customer.phone || ''}</small></td>
                  <td>{order.items.reduce((sum, item) => sum + item.quantity, 0)} units</td>
                  <td className="price-cell">₹{order.totalAmount.toFixed(2)}</td>
                  <td><select value={order.status} disabled={!(STATUS_TRANSITIONS[order.status] || []).length} onChange={(event) => setStatusChange({ order, status: event.target.value })} className={`status-select status-${order.status}`}><option value={order.status}>{statusLabel(order.status)}</option>{(STATUS_TRANSITIONS[order.status] || []).map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}</select></td>
                  <td>{new Date(order.createdAt).toLocaleString()}</td>
                  <td><div className="action-cell"><button className="btn-small btn-details" onClick={() => openDetails(order._id)}>Details</button><button className="btn-small btn-edit" disabled={Boolean(order.stockDeductedAt) || ['shipped', 'completed', 'cancelled'].includes(order.status)} onClick={() => openEditor(order)}>Edit</button><button className="btn-small btn-email" onClick={() => handleEmailStatus(order)}>Email</button><button className="btn-small btn-pdf" onClick={() => generateOrderPdf(order)}>PDF</button><button className="btn-small btn-delete" onClick={() => handleDeleteOrder(order)}>Delete</button></div></td>
                </tr>;
              })}</tbody>
            </table>
            {!visibleOrders.length && <p className="no-data">No matching orders.</p>}
          </div>
        </section>
      </div>

      {statusChange && <div className="modal-overlay"><div className="modal status-modal"><h3>Update order status</h3><p><strong>{statusChange.order.orderNumber || statusChange.order._id}</strong><br />New status: <strong>{statusLabel(statusChange.status)}</strong></p><label className="modal-label">Status note</label><input autoFocus value={statusNote} onChange={(event) => setStatusNote(event.target.value)} placeholder="Short customer-facing note" /><label className="modal-label description-label">Description</label><textarea rows={4} value={statusDescription} onChange={(event) => setStatusDescription(event.target.value)} placeholder="Describe this status update..." /><small>The date and time will be recorded automatically. Moving to Shipped deducts stock.</small><div className="modal-actions"><button disabled={saving} className="modal-confirm" onClick={submitStatusChange}>Save update</button><button className="modal-cancel" onClick={() => { setStatusChange(null); setStatusNote(''); setStatusDescription(''); }}>Cancel</button></div></div></div>}

      {selectedOrder && <div className="modal-overlay"><div className="modal order-modal"><div className="modal-header"><div><h3>{selectedOrder.orderNumber || selectedOrder._id}</h3><p>{new Date(selectedOrder.createdAt).toLocaleString()}</p></div><button className="modal-close" onClick={() => setSelectedOrder(null)}>×</button></div><div className="detail-grid"><div><span>Customer</span><strong>{customerFor(selectedOrder).name || 'Unknown'}</strong><small>{customerFor(selectedOrder).email || ''}</small><small>{customerFor(selectedOrder).phone || ''}</small></div><div><span>Status</span><strong className={`status-badge status-${selectedOrder.status}`}>{statusLabel(selectedOrder.status)}</strong><small>{selectedOrder.stockDeductedAt ? `Stock deducted ${new Date(selectedOrder.stockDeductedAt).toLocaleString()}` : 'Stock not deducted yet'}</small></div><div><span>Billing address</span><strong>{formatAddress(selectedOrder.billingAddress)}</strong></div><div><span>Shipping address</span><strong>{formatAddress(selectedOrder.shippingAddress)}</strong></div></div><h4>Items</h4><div className="order-detail-items">{selectedOrder.items.map((item, index) => { const book = bookOf(item, books); return <div key={`${bookIdOf(item)}-${index}`}><span>{book?.title || 'Book'} × {item.quantity}</span><strong>₹{(item.quantity * item.discountedPrice).toFixed(2)}</strong></div>; })}<div className="detail-total"><span>Total</span><strong>₹{selectedOrder.totalAmount.toFixed(2)}</strong></div></div><h4>Status history</h4><div className="status-timeline">{(selectedOrder.statusHistory || []).map((entry, index) => <div key={`${entry.changedAt}-${index}`}><span className="timeline-dot" /><div><strong>{statusLabel(entry.status)}{entry.note ? ` — ${entry.note}` : ''}</strong><p>{entry.description}</p><small>{new Date(entry.changedAt).toLocaleString()}{entry.changedBy ? ` · ${entry.changedBy}` : ''}</small></div></div>)}{!selectedOrder.statusHistory?.length && <p>No status history recorded for this older order.</p>}</div><div className="modal-actions"><button className="btn-primary" onClick={() => handleEmailStatus(selectedOrder)}>Email current status</button><button className="btn-primary" onClick={() => generateOrderPdf(selectedOrder)}>Download PDF</button><button className="btn-secondary" disabled={Boolean(selectedOrder.stockDeductedAt) || ['shipped', 'completed', 'cancelled'].includes(selectedOrder.status)} onClick={() => openEditor(selectedOrder)}>Edit items</button></div></div></div>}

      {editingOrder && <div className="modal-overlay"><div className="modal order-modal edit-modal"><div className="modal-header"><div><h3>Edit {editingOrder.orderNumber || editingOrder._id}</h3><p>Add, remove, or change item quantities.</p></div><button className="modal-close" onClick={() => setEditingOrder(null)}>×</button></div>{renderItemsEditor(editItems, setEditItems)}<div className="modal-actions"><button disabled={saving} className="btn-primary" onClick={saveEditedItems}>{saving ? 'Saving...' : 'Save items'}</button><button className="btn-secondary" onClick={() => setEditingOrder(null)}>Cancel</button></div></div></div>}
    </>
  );
};

export default OrderManagement;
